<script setup>
import { provide, onUnmounted, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import useLeads from "@/composables/use-leads";
import useChat from "@/composables/use-chat";
import { useJsSIP } from "@/composables/use-jssip";
import InCallBar from "@/components/v4/InCallBar.vue";
import CallEndedDialog from "@/components/v4/CallEndedDialog.vue";
/**
 * This component is only for invoking composition in vue 2 syntax
 * Will be removed (refactor) in the future
 */

// JsSIP — shared UA; standby REGISTER for inbound (see use-jssip resumeStandbySipRegistration)
const jssip = useJsSIP();
provide("jssip", jssip);

/** Register SIP early so inbound INVITE reaches the browser (extension = agent_id when set, else decoded user id). */
function tryStandbySipRegister() {
  try {
    if (typeof window === "undefined") return;
    jssip.resumeStandbySipRegistration?.();
  } catch (e) {
    console.warn("[jssip] Standby registration skipped:", e);
  }
}

let standbyRetryTimers = [];

function clearStandbyRetryTimers() {
  standbyRetryTimers.forEach((t) => clearTimeout(t));
  standbyRetryTimers = [];
}

function scheduleStandbyRetries() {
  clearStandbyRetryTimers();
  [2000, 8000].forEach((ms) => {
    const id = window.setTimeout(() => {
      if (jssip.isRegistered?.value) return;
      tryStandbySipRegister();
    }, ms);
    standbyRetryTimers.push(id);
  });
}

const route = useRoute();

const isLoginRoute = computed(() => {
  return (
    route.name === "login" || String(route.path || "").startsWith("/auth/login")
  );
});

const router = useRouter();

/**
 * SIP is per logged-in user but should only start after a project is chosen
 * (URL has :appId). Workspace /auth/login and /workspace stay without JsSIP.
 *
 * Do not run before `router.isReady()`: on hard refresh, `route.params` can be
 * incomplete during the first tick; an early `shutdownSip` would leave SIP off.
 */
function applySipRegistrationFromRoute() {
  const appId = route.params.appId;
  const login = isLoginRoute.value;
  const hasProject = appId != null && String(appId).trim() !== "";
  if (!hasProject || login) {
    clearStandbyRetryTimers();
    jssip.shutdownSip?.();
    return;
  }
  tryStandbySipRegister();
  scheduleStandbyRetries();
}

router.isReady().then(() => {
  applySipRegistrationFromRoute();
});

watch(
  () => ({
    appId: route.params.appId,
    login: isLoginRoute.value,
  }),
  () => {
    applySipRegistrationFromRoute();
  }
);

watch(
  () => jssip.isRegistered?.value,
  (registered) => {
    if (registered) clearStandbyRetryTimers();
  }
);

// Block navigation when call is in progress (reload, route change, back/forward)
let removeBeforeUnload = null;

const isCallInProgress = () => {
  const state = jssip?.callState?.value ?? "idle";
  return ["ringing", "connecting", "connected"].includes(state);
};

const handleBeforeUnload = (e) => {
  if (isCallInProgress()) {
    e.preventDefault();
    e.returnValue = "";
  }
};

watch(
  () => jssip?.callState?.value,
  (state) => {
    const inProgress = ["ringing", "connecting", "connected"].includes(state);
    if (inProgress) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      removeBeforeUnload = () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    } else if (removeBeforeUnload) {
      removeBeforeUnload();
      removeBeforeUnload = null;
    }
  },
  { immediate: true }
);

// Router guard: block navigation when call in progress
router.beforeEach((to, from, next) => {
  if (isCallInProgress()) {
    next(false);
  } else {
    next();
  }
});

onUnmounted(() => {
  if (removeBeforeUnload) {
    removeBeforeUnload();
  }
  clearStandbyRetryTimers();
});

useChat();
useLeads();
</script>

<template>
  <div class="vf-relative">
    <slot />
    <InCallBar />
    <CallEndedDialog />
  </div>
</template>
